import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import InputBase from '@material-ui/core/InputBase';
import Paper from '@material-ui/core/Paper';
import Hidden from '@material-ui/core/Hidden';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Typography } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { CircularProgress } from '@material-ui/core';

import { Section } from 'components/organisms';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: `0 ${theme.spacing(20)}px`,
    },
  },
  label: {
    fontSize: '26px',
    color: '#981248',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  inputContainer: {
    width: '100%',
    height: 52,
    display: 'flex',
    alignItems: 'center',
    borderColor: '#707070',
    backgroundColor: '#F3F3F3',
    borderRadius: theme.spacing(1.75),
  },
  input: {
    flex: 1,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(2),
    },
  },
  button: {
    fontSize: 21,
    fontWeight: 300,
    backgroundColor: '#273A42',
    color: '#FFFFFF',
    padding: 10,
    width: 150,
    height: 52,
    borderRadius: theme.spacing(1.75),
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
    '&:hover': {
      background: '#273A42',
    },
    [theme.breakpoints.down('sm')]: {
      width: 38,
    },
  },
  summary: {
    fontSize: 17,
    fontWeight: 300,
    color: '#981248',
    paddingTop: theme.spacing(3),
  },
  loading: {
    color: '#FFFFFF',
    margin: theme.spacing(0.5),
  },
}));

function SearchForm(props) {
  const classes = useStyles();
  const {
    title,
    placeholder,
    buttonText,
    resultSummary,
    onChange,
    defaultValue,
    loading,
  } = props;
  const [value, setValue] = useState(defaultValue || '');

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      onChange(event.target.value);
    }
  };

  const handleInputChange = event => {
    setValue(event.target.value);
  };

  const handleClearClick = event => {
    setValue('');
    onChange('');
  };

  return (
    <Section className={classes.root}>
      <Typography className={classes.label}>{title}</Typography>
      <Paper
        className={classes.inputContainer}
        elevation={0}
        variant="outlined"
      >
        <InputBase
          autoFocus
          className={classes.input}
          placeholder={placeholder}
          defaultValue={defaultValue}
          value={value}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          endAdornment={
            value ? (
              <IconButton onClick={handleClearClick}>
                <ClearIcon />
              </IconButton>
            ) : (
              <></>
            )
          }
        />
        <Button
          disabled={loading}
          type="submit"
          className={classes.button}
          onClick={() => onChange(value)}
          aria-label={buttonText}
        >
          <Hidden smDown>
            {loading ? (
              <CircularProgress className={classes.loading} />
            ) : (
              buttonText
            )}
          </Hidden>
          <Hidden mdUp>
            {loading ? (
              <CircularProgress className={classes.loading} />
            ) : (
              <SearchIcon />
            )}
          </Hidden>
        </Button>
      </Paper>
      <Typography className={classes.summary}>{resultSummary}</Typography>
    </Section>
  );
}

SearchForm.propTypes = {
  title: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  resultSummary: PropTypes.string,
  defaultValue: PropTypes.string,
  buttonText: PropTypes.string,
  loading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default SearchForm;
